import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import merge from 'lodash.merge';

import {
  ApolloLink,
  ApolloProvider,
  ApolloClient,
  ApolloCache,
  NormalizedCacheObject,
  Resolvers,
  InMemoryCacheConfig,
  InMemoryCache,
} from '@apollo/client';
import { authLink } from './links/authLink';
import { errorLink } from './links/errorLink';
import { httpAppsyncLink } from './links/httpAppsyncLink';
import { LocalApolloCache } from './cache';
import { ConfigContext } from '../ConfigContext';
import { SignInReporter } from '../../components/SignInReporter';
import { getValidToken } from '../../util/sessionExpiry';
import { Configuration } from '../ConfigContext/types';

import { notificationCacheConfig } from '../../graphql/notification/cache';
import { refVesselCacheConfig } from '../../graphql/vessels/cache';
import toastResolvers from '../../graphql/toasts/resolvers';
import portResolvers from '../../graphql/ports/resolvers';
import { refLocationCacheConfig } from '../../graphql/locations/cache';

const ClientsContext = createContext<ApolloClientsContextValue | null>(null);

export const useApolloClientsContext = () => {
  const context = useContext(ClientsContext);
  if (!context)
    throw new Error(
      'useClientsContext can only be used inside a ApolloClientsProvider',
    );
  return context as ApolloClientsContextValue;
};

export const ApolloClientsProvider: React.FC<ApolloClientsProviderProps> = ({
  children,
  clientSpecifiers,
}) => {
  const config = ConfigContext.useContainer();

  const communitySpecifier = useMemo(
    () => clientSpecifiers.find((spec) => spec.key === 'community'),
    [clientSpecifiers],
  );

  const clients = useMemo(() => {
    // Return null while config is loading to prevent accessing undefined properties
    if (!config || !config.aws) {
      return null;
    }

    const newClients = clientSpecifiers.reduce<
      ApolloClientsContextValue['clients']
    >((accumulator, specifier) => {
      return {
        ...accumulator,
        [specifier.key]: setupClient(
          config.aws,
          specifier.key,
          getValidToken,
          new InMemoryCache(
            specifier.cache
              ? merge(defaultCacheMap[specifier.key], specifier.cache)
              : defaultCacheMap[specifier.key],
          ),
          !!specifier.optional,
          specifier.resolvers,
          specifier.dev,
        ),
      };
    }, {});

    newClients.community = setupClient(
      config.aws,
      'community',
      getValidToken,
      new InMemoryCache(
        communitySpecifier?.cache ? communitySpecifier.cache : {},
      ),
      false,
      {
        ...communitySpecifier?.resolvers,
        Mutation: {
          ...toastResolvers.Mutation,
          ...portResolvers.Mutation,
        },
        Query: {
          ...portResolvers.Query,
        },
      },
    );

    return newClients;
  }, [clientSpecifiers, config, communitySpecifier]);

  if (!clients) return null;

  return (
    <ClientsContext.Provider value={{ clients }}>
      <ApolloProvider client={clients[clientSpecifiers[0].key]!}>
        <SignInReporter />
        {children}
      </ApolloProvider>
    </ClientsContext.Provider>
  );
};

function setupClient(
  configuration: Configuration['aws'],
  clientKey: string,
  getTokenFn: () => Promise<string>,
  cache: ApolloCache<NormalizedCacheObject>,
  optional: boolean,
  resolvers?: Resolvers,
  dev?: boolean,
) {
  if (!configuration[clientKey]) {
    if (optional) return undefined;
    throw new Error(
      `There is no client configuration for [${clientKey}], verify your config.json`,
    );
  }

  const link = ApolloLink.from([
    errorLink(),
    authLink(configuration[clientKey], getTokenFn),
    httpAppsyncLink(configuration[clientKey], getTokenFn),
  ]);

  const client = new ApolloClient({
    link,
    cache,
    resolvers,
    ...(dev === undefined ? {} : { connectToDevTools: dev }),
  });

  if (cache instanceof LocalApolloCache) {
    cache.populate();
    client.onResetStore(cache.populate);
  }

  return client;
}

// Default cache map
const defaultCacheMap: Record<ApolloClientKey, InMemoryCacheConfig> = {
  portCall: {},
  visitView: {},
  refPort: {},
  refVessel: refVesselCacheConfig,
  refLocation: refLocationCacheConfig,
  attachments: {},
  community: {},
  notificationBroker: notificationCacheConfig,
  broker: {},
  hydrometeo: {},
  visitNotification: {},
  mocker: {},
  agreements: {},
  reports: {},
  portDues: {},
  portDuesRef: {},
};

// ✅ Types
export interface ApolloClientsProviderProps {
  clientSpecifiers: ClientSpecifier[];
  children?: ReactNode; // added children
}

export interface ApolloClientsContextValue {
  clients: { [n in ApolloClientKey]?: ApolloClient<NormalizedCacheObject> };
}

export interface ClientSpecifier {
  key: ApolloClientKey;
  optional?: boolean;
  cache?: InMemoryCacheConfig;
  resolvers?: Resolvers;
  dev?: boolean;
}

export type ApolloClientKey =
  | 'portCall'
  | 'visitView'
  | 'refPort'
  | 'refVessel'
  | 'refLocation'
  | 'attachments'
  | 'community'
  | 'notificationBroker'
  | 'broker'
  | 'hydrometeo'
  | 'visitNotification'
  | 'mocker'
  | 'agreements'
  | 'reports'
  | 'portDues'
  | 'portDuesRef';
